import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthBrandHeading } from '@/auth/auth-brand-heading';
import { Label } from '@/components/ui/label';
import { CheckCircle2 } from 'lucide-react';
import { useAuthStore } from '@/auth/auth-store';
import { setPassword, ApiAuthError } from '@/auth/auth-api';
import { safeLogAuthError } from '@/auth/log-helpers';
import { track } from '@/shared/lib/analytics';
import {
  PASSWORD_RULES,
  allRulesPass,
  evaluateRules,
  passwordsMatch,
} from '@/auth/password-validation';

/**
 * Reset Password — New Password entry (Phase 3b of PROD-890).
 *
 * Flutter source:
 *   - `reset_password_new_password/presentation/widgets/body_widgets.dart`
 *   - `reset_password_new_password/presentation/bloc/reset_password_new_password_bloc.dart`
 *   - `create_password/data/datasource/create_password_data_source_impl.dart`
 *
 * Entry: `navigate('/reset-password-new-password', { state: { email, passwordResetToken } })`
 * from Phase 3a's access-code submit-success.
 *
 * Validation (verbatim Flutter `reset_password_new_password_bloc.dart:17-22`):
 *   valid = length >= 8 AND [0-9] AND [a-z] AND [@, _, !, $, %] AND [A-Z]
 *   status = valid && password === confirmPassword
 *
 * On success → navigate to `/password-changed` (Phase 6 route — falls back
 * to catch-all until Phase 6 lands).
 */
export function ResetPasswordNewPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);

  const incomingState = location.state as
    | { email?: string; passwordResetToken?: string }
    | undefined;
  const email = incomingState?.email ?? '';
  const passwordResetToken = incomingState?.passwordResetToken ?? '';

  const [password, setPwd] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (status === 'authenticated' || status === 'refreshing') {
    return <Navigate to="/" replace />;
  }

  if (!email || !passwordResetToken) {
    // Direct URL hit without prior access-code-verification state →
    // bounce back to the start of the flow.
    return <Navigate to="/forgot-password" replace />;
  }

  const ruleResults = evaluateRules(password);
  const allPass = allRulesPass(password);
  const match = passwordsMatch(password, confirmPassword);
  const showMismatch = !!confirmPassword && !match;
  const canSubmit = allPass && match && !isSubmitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await setPassword({ email, password, passwordResetToken });
      // Flutter `reset_password_new_password_bloc.dart:54-59`: fire on success.
      // (Event name carries Flutter's verbatim "Successfull" double-L typo.)
      track('Password Reset Successfull', { email });
      navigate('/password-changed', { replace: true });
    } catch (err) {
      // Flutter `reset_password_new_password_bloc.dart:45-50`: fire on failure.
      track('Password Reset Failed', {
        email,
        error: err instanceof Error ? err.message : String(err),
      });
      safeLogAuthError('Reset password — new password', err);
      // PROD-924 Q3: backend dropped passwordResetToken TTL from 24h
      // (ASP.NET default) to 1h (Scrips.Identity PR #491). 4xx becomes
      // more common — surface the same actionable wording the
      // create-password page uses instead of the generic Flutter
      // "Failed to create password". Token is invalidated via
      // SecurityStamp rotation on success too (single-use enforcement).
      if (err instanceof ApiAuthError && err.status >= 400 && err.status < 500) {
        toast.error(
          'Your reset link has expired or already been used. Click "Log in" and request a new one.',
        );
      } else {
        toast.error('Failed to create password');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sidebar px-4">
      <form
        onSubmit={onSubmit}
        className="w-90 max-w-full rounded-lg border border-border-default bg-surface-card p-8 flex flex-col gap-4"
        aria-label="Set a new password"
      >
        <AuthBrandHeading
          title="Create your password"
          subtitle="Choose a strong password to secure your account."
        />

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="new-password"
            className="text-xs font-medium text-text-secondary"
          >
            Create password
          </Label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            placeholder="Create password"
            value={password}
            onChange={(e) => setPwd(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        {/* 5-rule validator display — verbatim Flutter
            `reset_password_new_password/body_widgets.dart:48-130`. */}
        {allPass ? (
          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-status-success-text)]">
            <CheckCircle2 className="h-4 w-4" />
            Your password is secure
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm font-semibold">
            {PASSWORD_RULES.map((rule, i) => (
              <span
                key={rule.label}
                className={
                  ruleResults[i]
                    ? 'text-[var(--color-status-success-text)]'
                    : 'text-text-muted'
                }
              >
                {rule.label}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="confirm-password"
            className="text-xs font-medium text-text-secondary"
          >
            Confirm password
          </Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirm(e.target.value)}
            aria-invalid={showMismatch || undefined}
            aria-describedby={
              showMismatch ? 'confirm-password-error' : undefined
            }
            disabled={isSubmitting}
          />
          {showMismatch && (
            <span
              id="confirm-password-error"
              role="alert"
              className="text-sm text-status-error"
            >
              Password not matching
            </span>
          )}
        </div>

        <Button type="submit" disabled={!canSubmit} className="mt-2">
          {isSubmitting ? 'Saving…' : 'Save password'}
        </Button>

        <Link
          to="/login"
          className="self-center text-sm font-medium text-brand-primary hover:underline"
        >
          Log in
        </Link>
      </form>
    </div>
  );
}

export default ResetPasswordNewPasswordPage;
